/**
 * useAsync Hook
 *
 * Custom React hook for handling async operations with loading, error states, and cleanup
 *
 * @package WooAIChatbot
 * @author Roman Siromskyi <leowebmark@gmail.com>
 */
import { useState, useEffect, useCallback, useRef } from 'react';
/**
 * Custom hook for handling async operations
 *
 * Features:
 * - Automatic loading state management
 * - Error handling with typed errors
 * - Cleanup on component unmount
 * - Manual execution control
 * - State reset functionality
 * - Prevents state updates after unmount
 *
 * @template T - The type of data returned by the async function
 * @param asyncFunction - The async function to execute
 * @param dependencies - Optional array of dependencies to trigger re-execution
 * @param immediate - Whether to execute immediately on mount (default: true)
 * @returns Object containing data, loading, error states and control functions
 *
 * @example
 * ```tsx
 * const { data, loading, error, execute, reset } = useAsync(
 *   async () => playbooksApi.getAll(),
 *   []
 * );
 *
 * // Manual execution
 * await execute();
 *
 * // Reset state
 * reset();
 * ```
 */
export function useAsync(asyncFunction, dependencies = [], immediate = true) {
    const [state, setState] = useState({
        data: null,
        loading: immediate,
        error: null,
    });
    // Track if component is mounted to prevent state updates after unmount
    const isMountedRef = useRef(true);
    const executingRef = useRef(false);
    /**
     * Execute the async function
     */
    const execute = useCallback(async () => {
        // Prevent concurrent executions
        if (executingRef.current) {
            return;
        }
        executingRef.current = true;
        // Reset error and set loading state
        setState((prev) => ({
            ...prev,
            loading: true,
            error: null,
        }));
        try {
            const result = await asyncFunction();
            // Only update state if component is still mounted
            if (isMountedRef.current) {
                setState({
                    data: result,
                    loading: false,
                    error: null,
                });
            }
        }
        catch (err) {
            // Only update state if component is still mounted
            if (isMountedRef.current) {
                // Transform error to ApiError format
                const errorObj = err;
                const apiError = {
                    message: err instanceof Error ? err.message : 'An unknown error occurred',
                    code: errorObj?.code,
                    status: errorObj?.status,
                    data: errorObj?.data,
                };
                setState({
                    data: null,
                    loading: false,
                    error: apiError,
                });
            }
        }
        finally {
            executingRef.current = false;
        }
    }, [asyncFunction]);
    /**
     * Reset state to initial values
     */
    const reset = useCallback(() => {
        if (isMountedRef.current) {
            setState({
                data: null,
                loading: false,
                error: null,
            });
        }
    }, []);
    /**
     * Execute on mount or when dependencies change
     */
    useEffect(() => {
        // Reset mounted ref on each effect run (important for React StrictMode)
        isMountedRef.current = true;
        if (immediate) {
            execute();
        }
        // Cleanup function to prevent state updates after unmount
        return () => {
            isMountedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);
    return {
        ...state,
        execute,
        reset,
    };
}
